import React, {
  FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLanguage, type TranslationKey } from "../contexts/LanguageContext";
import { ReadingTheme } from "../services/readingPreferences";
import { supabase } from "../services/supabaseClient";

const MAX_FEEDBACK_LENGTH = 2000;
const countCodePoints = (value: string) => Array.from(value).length;

interface ReadingFeedbackProps {
  userId: string;
  adjustedReadingSectionId: string;
  theme: ReadingTheme;
}

interface FeedbackRow {
  comment: string;
}

const ReadingFeedback: React.FC<ReadingFeedbackProps> = ({
  userId,
  adjustedReadingSectionId,
  theme,
}) => {
  const { t } = useLanguage();
  const [draft, setDraft] = useState("");
  const [savedComment, setSavedComment] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const loadRequestId = useRef(0);
  const saveRequestId = useRef(0);
  const draftRevision = useRef(0);

  const loadFeedback = useCallback(async () => {
    const requestId = loadRequestId.current + 1;
    loadRequestId.current = requestId;
    setLoading(true);
    setLoadError(false);
    setSaving(false);
    setSaveError(false);
    setSaved(false);
    setDraft("");
    setSavedComment(null);

    const { data, error } = await supabase
      .from("reading_feedback")
      .select("comment")
      .eq("user_id", userId)
      .eq("adjusted_reading_section_id", adjustedReadingSectionId)
      .maybeSingle();

    if (requestId !== loadRequestId.current) return;
    if (error) {
      setLoadError(true);
    } else {
      const comment = (data as FeedbackRow | null)?.comment ?? "";
      setDraft(comment);
      setSavedComment(comment || null);
    }
    setLoading(false);
  }, [adjustedReadingSectionId, userId]);

  useEffect(() => {
    void loadFeedback();
    return () => {
      loadRequestId.current += 1;
      saveRequestId.current += 1;
    };
  }, [loadAttempt, loadFeedback]);

  const trimmedDraft = draft.trim();
  const trimmedDraftLength = countCodePoints(trimmedDraft);
  const draftLength = countCodePoints(draft);
  const validationKey: TranslationKey | null =
    trimmedDraftLength === 0
      ? "feedback.required"
      : trimmedDraftLength > MAX_FEEDBACK_LENGTH
        ? "feedback.tooLong"
        : null;
  const unchanged = savedComment !== null && trimmedDraft === savedComment;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (loading || saving || loadError || validationKey || unchanged) return;

    setSaving(true);
    setSaveError(false);
    setSaved(false);
    const requestId = saveRequestId.current + 1;
    saveRequestId.current = requestId;
    const submittedDraftRevision = draftRevision.current;
    const { error } = await supabase
      .from("reading_feedback")
      .upsert(
        {
          user_id: userId,
          adjusted_reading_section_id: adjustedReadingSectionId,
          comment: trimmedDraft,
        },
        { onConflict: "user_id,adjusted_reading_section_id" },
      )
      .select("comment")
      .single();

    if (requestId !== saveRequestId.current) return;
    if (error) {
      setSaveError(true);
    } else {
      setSavedComment(trimmedDraft);
      if (submittedDraftRevision === draftRevision.current) {
        setDraft(trimmedDraft);
        setSaved(true);
      }
    }
    setSaving(false);
  };

  const cardClassName =
    theme === "dark"
      ? "border-gray-700 bg-gray-800/60 text-gray-100"
      : theme === "sepia"
        ? "border-amber-200 bg-amber-100/50 text-stone-900"
        : "border-gray-200 bg-gray-50 text-gray-900";
  const inputClassName =
    theme === "dark"
      ? "border-gray-600 bg-gray-900 text-gray-100 placeholder:text-gray-500"
      : "border-gray-300 bg-white text-gray-900 placeholder:text-gray-400";
  const helperClassName = theme === "dark" ? "text-gray-300" : "text-gray-600";
  const errorClassName = theme === "dark" ? "text-red-300" : "text-red-700";
  const successClassName =
    theme === "dark" ? "text-green-300" : "text-green-800";

  return (
    <section
      className={`mb-6 rounded-lg border p-3 sm:p-4 ${cardClassName}`}
      aria-labelledby="reading-feedback-title"
    >
      <h2 id="reading-feedback-title" className="text-lg font-semibold">
        {t("feedback.title")}
      </h2>
      <p
        id="reading-feedback-help"
        className={`mt-1 text-sm ${helperClassName}`}
      >
        {t("feedback.body")}
      </p>

      {loading ? (
        <p className={`mt-3 text-sm ${helperClassName}`} role="status">
          {t("feedback.loading")}
        </p>
      ) : loadError ? (
        <div className="mt-3">
          <p role="alert" className={`text-sm ${errorClassName}`}>
            {t("feedback.loadError")}
          </p>
          <button
            type="button"
            onClick={() => setLoadAttempt((attempt) => attempt + 1)}
            className="mt-2 rounded-lg border border-current px-3 py-1.5 text-sm font-medium"
          >
            {t("feedback.retry")}
          </button>
        </div>
      ) : (
        <form className="mt-3" onSubmit={handleSubmit}>
          <label
            htmlFor="reading-feedback-comment"
            className="sr-only"
          >
            {t("feedback.comment")}
          </label>
          <textarea
            id="reading-feedback-comment"
            value={draft}
            rows={4}
            placeholder={t("feedback.placeholder")}
            aria-describedby="reading-feedback-help reading-feedback-count reading-feedback-message"
            onChange={(event) => {
              draftRevision.current += 1;
              setDraft(event.target.value);
              setSaved(false);
              setSaveError(false);
            }}
            className={`w-full rounded-lg border px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${inputClassName}`}
          />
          <div className="mt-1 flex items-start justify-between gap-4 text-sm">
            <div id="reading-feedback-message">
              {validationKey && draftLength > 0 && (
                <p role="alert" className={errorClassName}>
                  {t(validationKey)}
                </p>
              )}
              {saveError && (
                <p role="alert" className={errorClassName}>
                  {t("feedback.saveError")}
                </p>
              )}
              {saved && (
                <p role="status" className={successClassName}>
                  {t("feedback.saved")}
                </p>
              )}
            </div>
            <span id="reading-feedback-count" className={helperClassName}>
              {t("feedback.count", { count: draftLength })}
            </span>
          </div>
          <button
            type="submit"
            disabled={
              loading ||
              saving ||
              loadError ||
              Boolean(validationKey) ||
              unchanged
            }
            className="mt-3 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving
              ? t("feedback.saving")
              : t(
                  savedComment === null
                    ? "feedback.submit"
                    : "feedback.saveChanges",
                )}
          </button>
        </form>
      )}
    </section>
  );
};

export default ReadingFeedback;
